import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Send, ExternalLink, RefreshCw, Trash2, Loader2, X,
  CheckCircle2, AlertTriangle, Clock, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import ExtraSourcesEditor from "@/components/ExtraSourcesEditor";
import SourceLimitNote from "@/components/SourceLimitNote";
import SourceDateTag from "@/components/SourceDateTag";
import {
  TELEGRAM_TIP_RESERVED, fullMessage, overflowUrls, reservedLabel,
  slotsLeft, uniqueUrls,
} from "@/lib/sourceLimit";

const STATUS_META = {
  sourcing: {
    label: "Finding sources…",
    icon: Loader2, className: "text-[var(--ink-muted)]",
    spin: true,
  },
  ready: {
    label: "Ready to generate",
    icon: Sparkles, className: "text-[var(--accent)]",
  },
  generating: {
    label: "Generating…",
    icon: Loader2, className: "text-[var(--ink-muted)]", spin: true,
  },
  done: {
    label: "Article generated",
    icon: CheckCircle2, className: "text-emerald-600",
  },
  rejected: {
    label: "Rejected",
    icon: X, className: "text-[var(--ink-muted)]",
  },
  error: {
    label: "Error",
    icon: AlertTriangle, className: "text-red-600",
  },
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || {
    label: status, icon: Clock, className: "text-[var(--ink-muted)]",
  };
  const Icon = meta.icon;
  return (
    <span
      data-testid={`tip-status-${status}`}
      className={`inline-flex items-center gap-1.5 text-xs font-mono-display ${meta.className}`}
    >
      <Icon className={`w-3.5 h-3.5 ${meta.spin ? "animate-spin" : ""}`} />
      {meta.label}
    </span>
  );
}

function ManualPasteBox({ onCreated }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (text.trim().length < 20) {
      toast.error("Paste the full tip message.");
      return;
    }
    setBusy(true);
    try {
      const tip = await api.createManualTip(text);
      toast.success("Tip received — finding sources");
      setText("");
      onCreated?.(tip);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not parse tip");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border border-[var(--border)] bg-white p-4 sm:p-5">
      <div className="overline mb-2">Paste a tip manually</div>
      <p className="text-xs text-[var(--ink-muted)] mb-2">
        Format: <span className="font-mono">SOURCE:</span>{" "}
        <span className="font-mono">HEADLINE:</span>{" "}
        <span className="font-mono">FB COPY:</span>
      </p>
      <textarea
        data-testid="tip-manual-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={7}
        placeholder="SOURCE: Matador Network — https://…&#10;HEADLINE: In Los Cabos…&#10;&#10;FB COPY:&#10;Forget the steam room for a minute…"
        className="w-full border border-[var(--border)] px-3 py-2 text-sm font-mono-display leading-relaxed outline-none focus:border-[var(--ink)] bg-[var(--bg)]"
      />
      <div className="flex justify-end mt-2">
        <button
          data-testid="tip-manual-submit"
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
          Submit tip
        </button>
      </div>
    </div>
  );
}

function TipDetail({ tip, writers, onClose }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [writerKey, setWriterKey] = useState(
    tip.suggested_writer_key || (writers[0]?.key ?? "")
  );
  const [excluded, setExcluded] = useState(new Set());
  const [extraUrls, setExtraUrls] = useState([]);

  useEffect(() => {
    if (!tip.suggested_writer_key && writers.length > 0) {
      setWriterKey(writers[0].key);
    } else if (tip.suggested_writer_key) {
      setWriterKey(tip.suggested_writer_key);
    }
  }, [tip.suggested_writer_key, writers]);

  // What routes/telegram.py will send, in its order: the tip's own
  // link, the included found sources, then manual extras. The tip text
  // itself takes one of the 8 writer slots (lib/sourceLimit.js).
  const sendUrls = uniqueUrls([
    tip.source_url,
    ...(tip.sources_found || [])
      .map((s) => s?.url)
      .filter((u) => u && !excluded.has(u)),
    ...extraUrls,
  ]);
  const freeSlots = slotsLeft(sendUrls.length, TELEGRAM_TIP_RESERVED);
  const overflow = overflowUrls(sendUrls, TELEGRAM_TIP_RESERVED);

  const toggleUrl = (u) => {
    // Re-including a dropped source uses a slot — refuse when full.
    if (excluded.has(u) && freeSlots === 0) {
      toast.error(fullMessage());
      return;
    }
    const next = new Set(excluded);
    next.has(u) ? next.delete(u) : next.add(u);
    setExcluded(next);
  };

  const generateMutation = useMutation({
    mutationFn: () =>
      api.generateFromTipAndWait(tip.id, {
        writer_key: writerKey,
        excluded_source_urls: Array.from(excluded),
        additional_source_urls: extraUrls,
      }),
    onSuccess: (article) => {
      toast.success("Article generated");
      qc.invalidateQueries({ queryKey: ["tips"] });
      qc.invalidateQueries({ queryKey: ["tip", tip.id] });
      if (article?.id) navigate(`/article/${article.id}`);
    },
    onError: (e) => {
      if (e?.isPromptOnly) return;
      toast.error(
        e?.response?.data?.detail || e.message || "Generation failed"
      );
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.rejectTip(tip.id),
    onSuccess: () => {
      toast.success("Tip rejected");
      qc.invalidateQueries({ queryKey: ["tips"] });
      onClose?.();
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => api.retryTip(tip.id),
    onSuccess: () => {
      toast.success("Retrying source discovery");
      qc.invalidateQueries({ queryKey: ["tips"] });
      qc.invalidateQueries({ queryKey: ["tip", tip.id] });
    },
  });

  return (
    <div
      data-testid="tip-detail-panel"
      className="fixed inset-y-0 right-0 w-full sm:w-[560px] bg-white border-l border-[var(--border)] z-40 overflow-y-auto shadow-xl"
    >
      <div className="sticky top-0 bg-white border-b border-[var(--border)] px-5 py-3 flex items-center justify-between">
        <StatusPill status={tip.status} />
        <button
          data-testid="tip-detail-close"
          onClick={onClose}
          className="p-1 hover:bg-[var(--secondary)]"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <div className="overline mb-1">Headline</div>
          <div className="font-serif-display text-2xl leading-snug">
            {tip.headline}
          </div>
        </div>

        <div>
          <div className="overline mb-1">Seed source</div>
          <a
            href={tip.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:underline break-all"
          >
            {tip.source_name || tip.source_url}
            <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
        </div>

        {tip.fb_copy && (
          <div>
            <div className="overline mb-1">FB copy</div>
            <div className="text-sm bg-[var(--bg)] border border-[var(--border)] p-3 whitespace-pre-wrap">
              {tip.fb_copy}
            </div>
          </div>
        )}

        {tip.photo_credit && (
          <div className="text-xs text-[var(--ink-muted)]">
            <span className="overline mr-2">Photo credit</span>
            {tip.photo_credit}
          </div>
        )}

        {tip.status === "error" && tip.error && (
          <div className="border border-red-200 bg-red-50 text-red-800 text-xs p-3">
            <strong className="block mb-1">Enrichment error</strong>
            {tip.error}
          </div>
        )}

        <div>
          <div className="overline mb-2">
            Sources found ({tip.sources_found?.length || 0})
          </div>
          {tip.sources_found?.length ? (
            <ul className="space-y-2">
              {tip.sources_found.map((s) => {
                const drop = excluded.has(s.url);
                return (
                  <li
                    key={s.url}
                    data-testid={`tip-source-${btoa(s.url).slice(0, 12)}`}
                    className={`border border-[var(--border)] p-3 ${
                      drop ? "opacity-40 line-through" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-[var(--ink-muted)] font-mono-display truncate">
                          {s.source || new URL(s.url).host}
                          {" · "}
                          <SourceDateTag publishedAt={s.published_at} />
                        </div>
                        <div className="text-sm leading-snug">
                          {s.title || s.url}
                        </div>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-[var(--accent)] hover:underline break-all"
                        >
                          {s.url}
                        </a>
                        {!drop && overflow.includes(s.url) && (
                          <span
                            title="Past the 8-source limit — the writer won't receive this one"
                            className="ml-2 inline-block text-[9px] uppercase tracking-wider px-1 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 font-mono-display"
                          >
                            not sent
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => toggleUrl(s.url)}
                        className="text-xs px-2 py-1 border border-[var(--border)] hover:bg-[var(--secondary)] shrink-0"
                      >
                        {drop ? "Include" : "Drop"}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="text-xs text-[var(--ink-muted)] italic">
              {tip.status === "sourcing"
                ? "Discovering sources…"
                : "No additional sources found."}
            </div>
          )}
        </div>

        {tip.status !== "done" && tip.status !== "rejected" && (
          <div className="space-y-2">
            <SourceLimitNote
              count={sendUrls.length}
              reserved={TELEGRAM_TIP_RESERVED}
              reservedText={reservedLabel({ brief: true, briefName: "the tip text" })}
              testId="tip-source-limit"
            />
            <ExtraSourcesEditor
              urls={extraUrls}
              onChange={setExtraUrls}
              testIdPrefix="tip-extra-source"
              maxUrls={extraUrls.length + freeSlots}
            />
          </div>
        )}

        {tip.article_id && (
          <div>
            <Link
              to={`/article/${tip.article_id}`}
              className="inline-flex items-center gap-1.5 text-sm text-[var(--accent)] hover:underline"
            >
              Open generated article
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
        )}

        {tip.status !== "done" && tip.status !== "rejected" && (
          <div className="border-t border-[var(--border)] pt-4 space-y-3">
            <div>
              <label className="overline block mb-1">Writer</label>
              <select
                data-testid="tip-writer-select"
                value={writerKey}
                onChange={(e) => setWriterKey(e.target.value)}
                className="w-full border border-[var(--border)] px-3 py-2 text-sm bg-white outline-none focus:border-[var(--ink)]"
              >
                {writers.map((w) => (
                  <option key={w.key} value={w.key}>
                    {w.name}
                    {w.key === tip.suggested_writer_key ? "  ✱ suggested" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="tip-generate-button"
                onClick={() => generateMutation.mutate()}
                disabled={
                  generateMutation.isPending ||
                  tip.status === "sourcing" ||
                  tip.status === "generating" ||
                  !writerKey
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
              >
                {generateMutation.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5" />
                )}
                Generate article
              </button>
              {tip.status === "error" && (
                <button
                  data-testid="tip-retry-button"
                  onClick={() => retryMutation.mutate()}
                  disabled={retryMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Retry
                </button>
              )}
              <button
                data-testid="tip-reject-button"
                onClick={() => rejectMutation.mutate()}
                disabled={rejectMutation.isPending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:bg-[var(--secondary)]"
              >
                <X className="w-3.5 h-3.5" />
                Reject
              </button>
            </div>
          </div>
        )}

        {tip.raw_text && (
          <details className="text-xs text-[var(--ink-muted)]">
            <summary className="cursor-pointer">Show raw tip text</summary>
            <pre className="mt-2 whitespace-pre-wrap font-mono-display text-[11px] bg-[var(--bg)] border border-[var(--border)] p-2">
              {tip.raw_text}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

export default function TelegramTipsPage() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("all");

  const { data: tips = [], isLoading } = useQuery({
    queryKey: ["tips", filter],
    queryFn: () => api.listTips(filter === "all" ? undefined : filter),
    refetchInterval: (q) => {
      const list = q.state.data || [];
      // Auto-poll while any tip is still enriching/generating.
      const busy = list.some((t) =>
        ["sourcing", "generating"].includes(t.status)
      );
      return busy ? 3000 : false;
    },
  });

  const { data: writersData = [] } = useQuery({
    queryKey: ["writers"],
    queryFn: api.listWriters,
    staleTime: 60_000,
  });

  const { data: tgStatus } = useQuery({
    queryKey: ["tgStatus"],
    queryFn: api.telegramStatus,
    staleTime: 30_000,
  });

  const selected = useMemo(
    () => (selectedId ? tips.find((t) => t.id === selectedId) : null),
    [selectedId, tips]
  );

  const counts = useMemo(() => {
    const c = { all: tips.length };
    for (const t of tips) c[t.status] = (c[t.status] || 0) + 1;
    return c;
  }, [tips]);

  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteTip(id),
    onSuccess: () => {
      toast.success("Tip deleted");
      qc.invalidateQueries({ queryKey: ["tips"] });
    },
  });

  const filters = [
    { key: "all", label: "All" },
    { key: "ready", label: "Ready" },
    { key: "sourcing", label: "Sourcing" },
    { key: "done", label: "Generated" },
    { key: "rejected", label: "Rejected" },
    { key: "error", label: "Errors" },
  ];

  return (
    <div className="flex-1 overflow-y-auto min-w-0">
      <div className="px-4 sm:px-10 py-6 sm:py-8 border-b border-[var(--border)] bg-white">
        <div className="overline">Newsroom · tips</div>
        <h1 className="font-serif-display text-3xl sm:text-4xl mt-1">
          Telegram tips
        </h1>
        <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-2xl">
          Story leads forwarded from your Telegram channel. We fetch the seed
          URL, find related sources, and pick a writer — you review and
          generate in one click.
        </p>
        {tgStatus && !tgStatus.enabled && (
          <div className="mt-3 text-xs text-[var(--ink-muted)] border border-dashed border-[var(--border)] bg-[var(--bg)] p-3 max-w-2xl">
            No Telegram bot configured yet.{" "}
            <Link
              to="/settings"
              className="text-[var(--accent)] underline"
            >
              Configure one in Settings
            </Link>{" "}
            to receive tips automatically, or paste one below to test.
          </div>
        )}
        {tgStatus?.enabled && (
          <div className="mt-3 text-xs text-[var(--ink-muted)] font-mono-display">
            Bot: @{tgStatus.bot_username} · webhook{" "}
            {tgStatus.webhook_info?.pending_update_count !== undefined
              ? `${tgStatus.webhook_info.pending_update_count} pending`
              : "active"}
          </div>
        )}
      </div>

      <div className="px-4 sm:px-10 py-5 space-y-4">
        <ManualPasteBox
          onCreated={() => qc.invalidateQueries({ queryKey: ["tips"] })}
        />

        <div className="flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              data-testid={`tips-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1 text-xs border ${
                filter === f.key
                  ? "bg-[var(--slate-900)] text-white border-[var(--slate-900)]"
                  : "border-[var(--border)] hover:bg-[var(--secondary)]"
              }`}
            >
              {f.label}
              {counts[f.key] ? (
                <span className="ml-1 opacity-70">({counts[f.key]})</span>
              ) : null}
            </button>
          ))}
        </div>

        <div className="border border-[var(--border)] bg-white">
          {isLoading ? (
            <div className="p-6 text-sm text-[var(--ink-muted)] flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading tips…
            </div>
          ) : tips.length === 0 ? (
            <div
              data-testid="tips-empty-state"
              className="p-10 text-center text-sm text-[var(--ink-muted)]"
            >
              No tips yet. Forward a message to your Telegram bot or paste one
              above.
            </div>
          ) : (
            <ul>
              {tips.map((t) => (
                <li
                  key={t.id}
                  data-testid={`tip-row-${t.id}`}
                  onClick={() => setSelectedId(t.id)}
                  className={`px-4 sm:px-5 py-4 border-b border-[var(--border)] last:border-b-0 cursor-pointer hover:bg-[var(--secondary)] ${
                    selectedId === t.id ? "bg-[var(--secondary)]" : ""
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-serif-display text-base leading-snug truncate">
                        {t.headline}
                      </div>
                      <div className="text-xs text-[var(--ink-muted)] font-mono-display mt-1 truncate">
                        {t.source_name || (t.source_url ? new URL(t.source_url).host : "no source")}
                        {" · "}
                        {relTime(t.created_at)}
                        {t.telegram_from ? ` · via ${t.telegram_from}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusPill status={t.status} />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            window.confirm(
                              "Delete this tip permanently? " +
                                "(Generated article, if any, is unaffected.)"
                            )
                          ) {
                            deleteMutation.mutate(t.id);
                          }
                        }}
                        className="p-1 text-[var(--ink-muted)] hover:text-red-600"
                        title="Delete tip"
                        data-testid={`tip-delete-${t.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selected && (
        <TipDetail
          tip={selected}
          writers={writersData}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
